// @flow

import { getEntries, getValues } from '../../helpers';
import {
  getDoorCount,
  getActivePdbId,
  getEngine,
  getFueltype,
  getPower,
  getTransmission,
  getConsumption,
  getWeight,
  getPrice,
} from '../pdb/selectors';

import {
  getComparePopupTemplateBlacklist,
  getRemoveComparisonText,
  getAddComparisonText,
} from '../compareSettings/selectors';

import {
  getActiveClaimFree,
  getActiveColor,
  getActiveDuration,
  getActiveMileage,
  getActivePackage,
} from '../configuration/actives';

import type { State, TechSpecs, PdbPrices } from './types';
import { type State as PdbState } from '../pdb';
import { getTemplate } from '../routes/selectors';

import type { State as ConfigState } from '../configuration/types';
import type { State as RouterState } from '../router';
import type { State as RoutesState } from '../routes';
import type { State as CompareSettingsState } from '../compareSettings';
import type { OrderFormState } from '../orderForm/types';

type GlobalState = {
  pdbComparison: State,
  pdb: PdbState,
  configuration: ConfigState,
  router: RouterState,
  routes: RoutesState,
  compareSettings: CompareSettingsState,
  orderForm: OrderFormState,
};

const MAX_IN_PDB_COMPARISON = 3;

const pdbComparison = (state: GlobalState): State => (
  state.pdbComparison
);

export const getPdbIds = (state: GlobalState): Array<string> => (
  pdbComparison(state)
);

export const numPdbsInComparison = (state: GlobalState): number => (
  getPdbIds(state).length
);

export const isPdbComparisonFull = (state: GlobalState) => (
  numPdbsInComparison(state) === MAX_IN_PDB_COMPARISON
);

export function isPdbIdInComparison(state: GlobalState, pdbId?: string): boolean {
  const id = pdbId || getActivePdbId(state);

  if (!id) {
    throw Error('No active pdb item found');
  }

  return (new Set(getPdbIds(state))).has(id);
}

export const getComparisonText = (state: GlobalState, pdbId?: string) => {
  if (isPdbIdInComparison(state, pdbId)) {
    return getRemoveComparisonText(state);
  }
  return getAddComparisonText(state);
};

const compareSelectorMap = {
  claim_free: getActiveClaimFree,
  colors: getActiveColor,
  durations: getActiveDuration,
  mileages: getActiveMileage,
  packages: getActivePackage,
};

const getComparePrice = (state: GlobalState, pdbId?: ?string): number => (
  getValues(compareSelectorMap)
    .reduce((totalPrice, selector) => {
      const result = selector(state, pdbId);

      if (!result) {
        return totalPrice;
      }

      if (!result.price) {
        return totalPrice;
      }

      const newValue = totalPrice + Number(result.price);
      return newValue;
    }, 0)
);

export const getPricesForPdbIds = (state: GlobalState): PdbPrices => (
  getPdbIds(state)
    .reduce((acc, pdbId) => ([
      ...acc,
      {
        price: parseInt(getPrice(state, pdbId), 10) + getComparePrice(state, pdbId),
        pdbId,
      },
    ]), [])
);

export const getComparedPricesForPdbIds = (state: GlobalState): Array<number> => {
  const pricesForPdbIds = getPricesForPdbIds(state);
  if (pricesForPdbIds.length === 0) {
    return [];
  }

  const basePrice = pricesForPdbIds[0].price;
  return pricesForPdbIds.reduce((acc, { price }) => {
    const difference = price - basePrice;
    return [
      ...acc,
      difference,
    ];
  }, []);
};

export const getBasePricesForPdbIds = (state: GlobalState): PdbPrices => (
  getPdbIds(state)
    .reduce((acc, pdbId) => ([
      ...acc,
      {
        price: parseInt(getPrice(state, pdbId), 10),
        pdbId,
      },
    ]), [])
);

const specs = {
  engine: getEngine,
  fuelType: getFueltype,
  power: getPower,
  consumption: getConsumption,
  transmission: getTransmission,
  doors: getDoorCount,
  weight: (state: GlobalState, pdbId: string) => `${getWeight(state, pdbId)} kg`, // @todo this one is dependent on the selected package
};

export const getTechSpecsForPdbIds = (state: GlobalState): TechSpecs => {
  const pdbIds = getPdbIds(state);
  return getEntries(specs)
    .reduce((acc, [key, specSelector]) => ({
      ...acc,
      [key.valueOf()]: pdbIds.map(id => specSelector(state, id)),
    }), {});
};

export const shouldShowPopup = (state: GlobalState): boolean => (
  !getComparePopupTemplateBlacklist(state)
    .includes(getTemplate(state))
);
